import { type DependencyConfidence, type DependencyFamily, type DependencyGroup, type LoopDependency, dependencyFamily, dependencyFamilyLabels, unconfirmedFor } from "../../shared/dependencies";
import { providerFromString } from "../brands";
import { providerName } from "./loopPlacements";

/** Client-side shapes for a loop's dependencies; the API shapes mirror src/worker/dependencies.ts. */

/** What the loop list carries per loop: a count of identities and up to three required names. */
export type LoopDependencySummary = { count: number; required: string[] };

export type DependenciesResponse = { entries: LoopDependency[]; groups: DependencyGroup[] };

/** On a planned placement: how many of the loop's dependencies the target environment has confirmed. */
export type PlacementReadiness = { provider: string; confirmed: number; total: number; missing: DependencyGroup[] };

export type DependencyFamilySection = { family: DependencyFamily; label: string; groups: DependencyGroup[] };

export type ChecklistItem = { group: DependencyGroup; confirmed: boolean };

const FAMILY_ORDER: DependencyFamily[] = ["tools", "services", "secrets", "code", "models", "other"];
const FACET_LIMIT = 5;

/** Rows grouped by family in the fixed order (Tools, Services, Secrets, Code and data, Models, Other); families without a row are omitted. */
export function familySections(groups: DependencyGroup[]): DependencyFamilySection[] {
  const byFamily = new Map<DependencyFamily, DependencyGroup[]>();
  for (const group of groups) {
    const family = dependencyFamily(group.kind);
    const rows = byFamily.get(family);
    if (rows) rows.push(group);
    else byFamily.set(family, [group]);
  }
  return FAMILY_ORDER.filter((family) => byFamily.has(family)).map((family) => ({ family, label: dependencyFamilyLabels[family], groups: byFamily.get(family)! }));
}

/** The panel head: "{n} · {required} required", or the empty case. */
export function dependencyHeadline(groups: DependencyGroup[]): string {
  if (!groups.length) return "None recorded";
  return `${groups.length} · ${groups.filter((group) => group.required).length} required`;
}

/** The "Needs" line on a loop card: the required names the list carries, then how many more there are. Null when nothing is recorded. */
export function needsLine(summary: LoopDependencySummary | null | undefined): string | null {
  if (!summary || summary.count <= 0) return null;
  const names = summary.required.filter(Boolean);
  if (!names.length) return `Needs · ${summary.count} optional`;
  const rest = summary.count - names.length;
  return ["Needs", ...names, ...(rest > 0 ? [`+${rest}`] : [])].join(" · ");
}

/** Facet counts for the Loops list: the top five required names across the loops, most common first, then by name. */
export function dependencyFacets(loops: Array<{ dependencies?: LoopDependencySummary | null }>): Array<{ name: string; count: number }> {
  const counts = new Map<string, { name: string; count: number }>();
  for (const loop of loops) {
    for (const name of new Set((loop.dependencies?.required ?? []).map((value) => value.trim()).filter(Boolean))) {
      const key = name.toLowerCase();
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { name, count: 1 });
    }
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)).slice(0, FACET_LIMIT);
}

/** Whether a loop's list summary names this dependency (case-insensitive). */
export function needsDependency(loop: { dependencies?: LoopDependencySummary | null }, name: string): boolean {
  const needle = name.trim().toLowerCase();
  return (loop.dependencies?.required ?? []).some((value) => value.trim().toLowerCase() === needle);
}

/** The dependency names a loop carries, for the search haystack. */
export function dependencySearchText(loop: { dependencies?: LoopDependencySummary | null }): string {
  return (loop.dependencies?.required ?? []).join(" ");
}

/** The "{Target} must provide" checklist for a move: every dependency, marked by whether the target environment has already confirmed it. */
export function moveChecklist(groups: DependencyGroup[], target: string): ChecklistItem[] {
  const missing = new Set(unconfirmedFor(groups, target));
  return groups.map((group) => ({ group, confirmed: !missing.has(group) }));
}

/** The planned-placement line once the worker reports readiness; null when it did not, so the caller keeps its plain wording. Lowercase to sit in the placement row's mono line. */
export function readinessLine(readiness: PlacementReadiness | null | undefined): string | null {
  if (!readiness) return null;
  const lead = "waiting for its first run";
  if (readiness.total <= 0) return `${lead} · no dependencies recorded`;
  return `${lead} · ${readiness.confirmed} of ${readiness.total} ${readiness.total === 1 ? "dependency" : "dependencies"} confirmed`;
}

/** The confidence one environment gave a dependency, from its own row; the group's confidence when it has none. */
export function providerConfidence(group: DependencyGroup, provider: string): DependencyConfidence {
  return group.entries.find((entry) => entry.provider === provider)?.confidence ?? group.confidence;
}

/** Hover title for an environment's mark on a dependency row: "Confirmed by Claude", or "Reported by Codex" when it only inferred it. */
export function providerMarkTitle(group: DependencyGroup, provider: string): string {
  const name = providerName(providerFromString(provider) ?? "other");
  const confidence = providerConfidence(group, provider);
  if (confidence === "confirmed") return `Confirmed by ${name}`;
  if (confidence === "inferred") return `Reported by ${name}`;
  return `Declared for ${name}`;
}
